import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { fetchWithAuth } from '../utils/api';
import { useLanguage } from './LanguageContext';
import { calculateLine, resolveVatRate } from '../utils/vat';

const MEAL_TOKENS = [
  ['morning', 'meal_morning', 'Morning'],
  ['noon',    'meal_noon',    'Noon'],
  ['evening', 'meal_evening', 'Evening'],
  ['bedtime', 'meal_bedtime', 'Bedtime'],
];

export const BLANK_LINE = {
  medication_id: '', quantity_prescribed: '', dose_per_time: '', frequency: '',
  duration_days: '', administration_route: 'oral', before_food: 0, after_food: 0,
  meal_times: '', special_instructions: '', unit_price: '', vat_mode: 'none', vat_rate: '',
};

const PrescriptionEditor = ({ value = [], onChange, branchId, branchVatRate = 7, disabled = false }) => {
  const { print_lang, currentLanguage } = useLanguage();
  const [medications, setMedications] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [routes, setRoutes] = useState([]);

  useEffect(() => {
    if (!branchId) return;
    fetchWithAuth(`/api/medications?branch_id=${branchId}&status=active`)
      .then((r) => setMedications(r?.data || [])).catch(() => setMedications([]));
    fetchWithAuth('/api/medications/frequencies')
      .then((r) => setFrequencies(r?.data || [])).catch(() => setFrequencies([]));
    fetchWithAuth('/api/medications/routes')
      .then((r) => setRoutes(r?.data || [])).catch(() => setRoutes([]));
  }, [branchId]);

  const medById = useMemo(
    () => new Map(medications.map((m) => [String(m.medication_id), m])),
    [medications]
  );

  const label = (row) => (currentLanguage === 'th' ? (row.name_th || row.name_en) : row.name_en);

  const patch = (idx, changes) => {
    const next = value.map((l, i) => (i === idx ? { ...l, ...changes } : l));
    onChange(next);
  };

  const addLine = () => onChange([...value, { ...BLANK_LINE }]);
  const removeLine = (idx) => onChange(value.filter((_, i) => i !== idx));

  // Choosing a medication adopts its catalog price and VAT treatment. The
  // dentist can still override the price on the line afterwards.
  const chooseMedication = (idx, medicationId) => {
    const med = medById.get(String(medicationId));
    patch(idx, {
      medication_id: medicationId,
      unit_price: med ? med.price_per_unit : '',
      vat_mode: med ? med.vat_mode : 'none',
      vat_rate: med && med.vat_rate !== null ? med.vat_rate : '',
    });
  };

  // dose x times/day x days. Only offered when the frequency has a fixed
  // schedule — 'as needed' and 'once' cannot be projected.
  const suggestQuantity = (line) => {
    const freq = frequencies.find((f) => f.code === line.frequency);
    const perDay = freq && freq.times_per_day !== null ? Number(freq.times_per_day) : null;
    const dose = Number(line.dose_per_time);
    const days = Number(line.duration_days);
    if (!perDay || !Number.isFinite(dose) || dose <= 0 || !Number.isFinite(days) || days <= 0) {
      return null;
    }
    return Math.ceil(dose * perDay * days);
  };

  const lineTotal = (line) => {
    if (line.unit_price === '' || line.unit_price === undefined) return 0;
    const rate = resolveVatRate({
      vatMode: line.vat_mode, itemRate: line.vat_rate, branchRate: branchVatRate,
    });
    return calculateLine({
      quantity: line.quantity_prescribed || 0,
      unit_price: line.unit_price,
      vat_mode: line.vat_mode,
      vat_rate: rate,
    }).total;
  };

  const grandTotal = value.reduce((sum, l) => sum + lineTotal(l), 0);

  return (
    <div>
      {value.length === 0 && (
        <p className="text-sm text-gray-500 mb-2">
          {print_lang('no_medications_on_note', 'No medications prescribed')}
        </p>
      )}

      {value.map((line, idx) => {
        const med = medById.get(String(line.medication_id));
        const suggested = suggestQuantity(line);
        return (
          <div key={idx} className="border rounded p-3 mb-3 bg-gray-50">
            <div className="grid grid-cols-12 gap-2 mb-2">
              <div className="col-span-5">
                <label className="block text-xs text-gray-500 mb-1">
                  {print_lang('medication', 'Medication')}
                </label>
                <select
                  className="w-full border rounded px-2 py-1" disabled={disabled}
                  value={line.medication_id}
                  onChange={(e) => chooseMedication(idx, e.target.value)}
                >
                  <option value="">-</option>
                  {medications.map((m) => (
                    <option key={m.medication_id} value={m.medication_id}>
                      {m.name}{m.strength ? ` ${m.strength}` : ''} ({m.unit})
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-gray-500 mb-1">
                  {print_lang('dose_per_time', 'Dose')}
                </label>
                <input type="text" className="w-full border rounded px-2 py-1" disabled={disabled}
                       value={line.dose_per_time}
                       onChange={(e) => patch(idx, { dose_per_time: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-gray-500 mb-1">
                  {print_lang('frequency', 'Frequency')}
                </label>
                <select className="w-full border rounded px-2 py-1" disabled={disabled}
                        value={line.frequency}
                        onChange={(e) => patch(idx, { frequency: e.target.value })}>
                  <option value="">-</option>
                  {frequencies.map((f) => (
                    <option key={f.code} value={f.code}>{label(f)}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-gray-500 mb-1">
                  {print_lang('duration_days', 'Duration (days)')}
                </label>
                <input type="number" min="1" className="w-full border rounded px-2 py-1"
                       disabled={disabled} value={line.duration_days}
                       onChange={(e) => patch(idx, { duration_days: e.target.value })} />
              </div>
              <div className="col-span-1 flex items-end justify-end">
                <button type="button" className="p-2 hover:bg-red-50 rounded" disabled={disabled}
                        onClick={() => removeLine(idx)} title={print_lang('delete', 'Delete')}>
                  <Trash2 size={16} className="text-red-600" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-2 mb-2">
              <div className="col-span-3">
                <label className="block text-xs text-gray-500 mb-1">
                  {print_lang('quantity', 'Quantity')}
                  {med ? ` (${med.unit})` : ''}
                </label>
                <div className="flex gap-1">
                  <input type="number" step="0.01" min="0" className="w-full border rounded px-2 py-1"
                         disabled={disabled} value={line.quantity_prescribed}
                         onChange={(e) => patch(idx, { quantity_prescribed: e.target.value })} />
                  {suggested !== null && String(suggested) !== String(line.quantity_prescribed) && (
                    <button type="button" className="text-xs px-2 border rounded whitespace-nowrap"
                            disabled={disabled}
                            onClick={() => patch(idx, { quantity_prescribed: suggested })}>
                      {print_lang('suggested_quantity', 'Suggested')}: {suggested}
                    </button>
                  )}
                </div>
              </div>
              <div className="col-span-3">
                <label className="block text-xs text-gray-500 mb-1">
                  {print_lang('administration_route', 'Route')}
                </label>
                <select className="w-full border rounded px-2 py-1" disabled={disabled}
                        value={line.administration_route}
                        onChange={(e) => patch(idx, { administration_route: e.target.value })}>
                  {routes.map((r) => (
                    <option key={r.code} value={r.code}>{label(r)}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-3">
                <label className="block text-xs text-gray-500 mb-1">
                  {print_lang('food_timing', 'Food timing')}
                </label>
                {/* before_food and after_food are mutually exclusive, so one
                    control drives both rather than two checkboxes that can
                    contradict each other. */}
                <select
                  className="w-full border rounded px-2 py-1" disabled={disabled}
                  value={line.before_food === 1 ? 'before' : (line.after_food === 2 ? 'after_immediate' : (line.after_food === 1 ? 'after' : ''))}
                  onChange={(e) => {
                    const v = e.target.value;
                    patch(idx, {
                      before_food: v === 'before' ? 1 : 0,
                      after_food: v === 'after' ? 1 : (v === 'after_immediate' ? 2 : 0),
                    });
                  }}
                >
                  <option value="">{print_lang('food_not_set', 'Not specified')}</option>
                  <option value="before">{print_lang('before_food', 'Before food')}</option>
                  <option value="after">{print_lang('after_food', 'After food')}</option>
                  <option value="after_immediate">
                    {print_lang('after_food_immediate', 'Immediately after food')}
                  </option>
                </select>
              </div>
              <div className="col-span-3 text-right">
                <label className="block text-xs text-gray-500 mb-1">
                  {print_lang('total', 'Total')}
                </label>
                <div className="px-2 py-1 font-medium">{lineTotal(line).toFixed(2)}</div>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-5">
                <label className="block text-xs text-gray-500 mb-1">
                  {print_lang('meal_times', 'Meal times')}
                </label>
                <div className="flex gap-3">
                  {MEAL_TOKENS.map(([token, key, fallback]) => {
                    const selected = (line.meal_times || '').split(',').includes(token);
                    return (
                      <label key={token} className="flex items-center gap-1 text-sm">
                        <input type="checkbox" checked={selected} disabled={disabled}
                          onChange={(e) => {
                            const current = (line.meal_times || '').split(',').filter(Boolean);
                            const next = e.target.checked
                              ? [...current, token]
                              : current.filter((t) => t !== token);
                            // Keep canonical order so the label renderer is stable.
                            const ordered = MEAL_TOKENS
                              .map(([t]) => t).filter((t) => next.includes(t));
                            patch(idx, { meal_times: ordered.join(',') });
                          }} />
                        {print_lang(key, fallback)}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="col-span-7">
                <label className="block text-xs text-gray-500 mb-1">
                  {print_lang('special_instructions', 'Special instructions')}
                </label>
                <input type="text" className="w-full border rounded px-2 py-1" disabled={disabled}
                       value={line.special_instructions}
                       onChange={(e) => patch(idx, { special_instructions: e.target.value })} />
              </div>
            </div>
          </div>
        );
      })}

      <div className="flex justify-between items-center">
        <button type="button" className="btn flex items-center gap-2" disabled={disabled}
                onClick={addLine}>
          <Plus size={16} /> {print_lang('add_medication_line', 'Add Medication')}
        </button>
        {value.length > 0 && (
          <div className="text-right">
            <span className="text-sm text-gray-500 mr-2">{print_lang('total', 'Total')}</span>
            <span className="font-bold">{grandTotal.toFixed(2)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrescriptionEditor;
